import {ActionIcon, Paper, Select, TextInput} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import {ScalesIcon, TrashIcon, UsersIcon} from "@phosphor-icons/react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Row, Col} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import Page from "../../components/Page/Page.tsx";
import {useEntityTypes, useUser} from "../../utils/api.ts";
import style from "./Cargo.module.css";
import {CargoBar, DockingBayIcon, HangarBayIcon, LandingIcon} from "./Icons.tsx";
import useSavedItem from "../../components/SharedSave/useSavedItem.ts";
import SharedSave from "../../components/SharedSave/SharedSave.tsx";
import {useParams} from "react-router";
import CargoParser from "./CargoParser.tsx";
import {useSaveFitout} from "../../components/SharedSave/useSaveFitout.ts";
import {StatChanges, StatUpdateDialog} from "./StatUpdateDialog.tsx";
import {ShipType, VehicleType, MaterialType, DroidType, ItemType} from "../../types/swc.types.ts";
import {track} from "../../utils/analytics.ts";

type ContainerEntity = ShipType | VehicleType;
type CalculatedContainerEntity = ContainerEntity & { squad: number }
type CargoEntity = ShipType | VehicleType | MaterialType | DroidType | ItemType;
// Special entries: manual holds the total weight/volume, passengers only count against maxpassengers
type ManualEntity = { id: 'manual', name: string, weight: number, volume: number };
type PassengersEntity = { id: 'passengers', name: string, weight: 0, volume: 0 };
type EntityCargoKind = 'ship' | 'container' | 'vehicle' | 'material' | 'item' | 'droid' | 'not found';
type CargoType = {
  uuid: string,
  qty: number | null,
  entity: CargoEntity,
  type: EntityCargoKind
} | {
  uuid: string,
  qty: number | null,
  entity: ManualEntity,
  type: 'manual'
} | {
  uuid: string,
  qty: number | null,
  entity: PassengersEntity,
  type: 'passengers'
}

const CARGO_CONTAINER_CLASS = 'Cargo Containers';

const optContainerType = [
  {label: 'Ships', value: 'ships'},
  {label: 'Vehicles', value: 'vehicles'},
]
const optOrder = [
  {label: 'Alphabetical', value: 'alphabetical'},
  {label: 'Hyper speed', value: 'hyper'},
  {label: 'Sublight Speed', value: 'sublight'}
]

type SortShip = 'alphabetical' | 'hyper' | 'sublight';

const sorting = {
  alphabetical: (a: ContainerEntity, b: ContainerEntity) => a.name.localeCompare(b.name),
  hyper: (a: ShipType, b: ShipType) => b.speed.hyper - a.speed.hyper,
  sublight: (a: ShipType, b: ShipType) => b.speed.sublight - a.speed.sublight
};


const mapToOption = (entity: CargoEntity) => {
  return {label: entity.name, value: entity.id};
}
function CargoCalculator() {
  const {id} = useParams();
  const [order, setOrder] = useState<SortShip>('alphabetical');
  const [cargo, setCargo] = useState<CargoType[]>([]);
  const [containerType, setContainerType] = useState('ships');
  const {user} = useUser();

  const [updatedEntities, setUpdatedEntities] = useState<StatChanges[]>([]);
  const saveMutation = useSaveFitout('cargo', id);

  const {ships, vehicles, materials, items, droids, isPending} = useEntityTypes(['ships', 'vehicles', 'materials', 'items', 'droids'], true);

  const isReady = true;

  const addEntity = useCallback((id: string, qty: number | null = null) => {
    if (id === 'manual') {
      setCargo(prev => [...prev, {uuid: uuidv4(), entity: {id: 'manual', name: '', weight: 0, volume: 0}, qty: 1, type: 'manual'}]);
      return;
    }
    if (id === 'passengers') {
      setCargo(prev => [...prev, {uuid: uuidv4(), entity: {id: 'passengers', name: 'Passengers', weight: 0, volume: 0}, qty, type: 'passengers'}]);
      return;
    }
    let [t] = id.split(':');
    let entity: CargoEntity | undefined;
    let type: EntityCargoKind;
    if (t === '2') {
      entity = ships.find(s => s.id === id);
      type = entity?.class === CARGO_CONTAINER_CLASS ? 'container' : 'ship';
    } else if (t === '3') {
      entity = vehicles.find(s => s.id === id);
      type = 'vehicle';
    } else if (t === '16') {
      entity = materials.find(s => s.id === id);
      type = 'material';
    } else if (t === '12') {
      entity = items.find(s => s.id === id);
      type = 'item';
    } else if (t === '13') {
      entity = droids.find(s => s.id === id);
      type = 'droid';
    } else {
      console.error('Missing type map for ', id);
      type = 'not found';
    }
    if (typeof entity !== 'undefined') {
      setCargo(prev => [...prev, {uuid: uuidv4(), entity, qty, type}]);
    } else {
      console.error('Entity', type, id, 'not found');
    }
  }, [ships, vehicles, materials, items, droids]);

  const removeCargo = useCallback((uuid: string) => {
    setCargo(prev => prev.filter(c => c.uuid !== uuid));
  }, [])
  const updateQty = useCallback((uuid: string, qty: number | null) => {
    setCargo(prev => {
      let next = [...prev];
      let i = next.findIndex(c => c.uuid === uuid);
      next[i] = {...next[i], qty};
      return next;
    })
  }, []);
  const updateManual = useCallback((uuid: string, values: Partial<Omit<ManualEntity, 'id'>>) => {
    setCargo(prev => prev.map(c => c.uuid === uuid && c.type === 'manual' ? {...c, entity: {...c.entity, ...values}} : c));
  }, []);

  const totals = useMemo(() => {
    return cargo.reduce((tot, cargo) => {
      let w = tot.weight + ((cargo.qty ?? 0) * cargo.entity.weight);
      let v = tot.volume + ((cargo.qty ?? 0) * cargo.entity.volume);
      let ships = tot.ships || (cargo.type === 'ship');
      let vehicles = tot.vehicles || (cargo.type === 'vehicle');
      let containers = tot.containers || (cargo.type === 'container');
      let passengers = tot.passengers + (cargo.type === 'passengers' ? (cargo.qty ?? 0) : 0);
      // manual entries are bulk totals that can be split across a squad
      let divisible = cargo.type === 'manual';
      return {
        weight: w,
        volume: v,
        ships,
        vehicles,
        containers,
        passengers,
        maxWeight: divisible ? tot.maxWeight : Math.max(tot.maxWeight, cargo.entity.weight),
        maxVol: divisible ? tot.maxVol : Math.max(tot.maxVol, cargo.entity.volume),
      };
    }, {weight: 0, volume: 0, ships: false, vehicles: false, containers: false, passengers: 0, maxWeight: 0, maxVol: 0});
  }, [cargo]);


  const containers = useMemo(() => {
    if (totals.weight === 0 && totals.volume === 0 && totals.passengers === 0) {
      return [];
    }
    let c: ContainerEntity[] = [];
    if (containerType === 'ships') {
      c = ships;
    } else if (containerType === 'vehicles') {
      c = vehicles;
    }

    return c.filter(s => {
        if (totals.ships && !s.dockingbay) {
          return false;
        }
        if (totals.vehicles && !s.hangarbay) {
          return false;
        }
        // cargo containers fit in either a docking or a hangar bay
        if (totals.containers && !s.dockingbay && !s.hangarbay) {
          return false;
        }
        let squadNum = Math.floor(12 / s.slotsize);
        if (totals.passengers > squadNum * s.maxpassengers) {
          return false;
        }
        if (Math.ceil(totals.weight / squadNum) > s.weightcap) {
          return false;
        }
        if (Math.ceil(totals.volume / squadNum) > s.volumecap) {
          return false;
        }
        if (totals.maxWeight > s.weightcap) {
          return false;
        }
        if (totals.maxVol > s.volumecap) {
          return false;
        }

        return true;
      }
    ).map(el => {
      let entity = {...el} as CalculatedContainerEntity;
      let maxSquad = Math.floor(12 / entity.slotsize);
      if (maxSquad > 1 && (totals.weight > entity.weightcap || totals.volume > entity.volumecap || totals.passengers > entity.maxpassengers)) {
        let sqW = Math.ceil(totals.weight / entity.weightcap);
        let sqV = Math.ceil(totals.volume / entity.volumecap);
        let sqP = totals.passengers > 0 ? Math.ceil(totals.passengers / entity.maxpassengers) : 1;
        entity.squad = Math.max(sqW, sqV, sqP);
      } else {
        entity.squad = 1;
      }
      return entity;
      // @ts-expect-error (sort could be for Ships or Vehicles and it gets confused)
    }).sort(sorting[order]);
  }, [totals, containerType, order, ships, vehicles]);


  const treeOptions = useMemo(() => {
    return [
      {id: 'special', key: 'special', group: 'Special', items: [
        {label: 'Manual weight/volume', value: 'manual'},
        {label: 'Passengers', value: 'passengers'},
      ]},
      {id: 'ships', key: 'ships', group: 'Ships', items: ships.map(mapToOption)},
      {id: 'vehicles', key: 'vehicles', group: 'Vehicles', items: vehicles.map(mapToOption)},
      {id: 'materials', key: 'materials', group: 'Materials', items: materials.map(mapToOption)},
      {id: 'droids', key: 'droids', group: 'Droids (Crated)', items: droids.map(mapToOption)},
      {id: 'items', key: 'items', group: 'Items', items: items.map(mapToOption)}
    ];
  }, [ships, vehicles, materials, items, droids]);

  const savedData = useSavedItem('cargo', id);

  const [lastProcessedId, setLastProcessedId] = useState<string | null>(null);

  useEffect(() => {
    if (savedData && savedData.content && !isPending && lastProcessedId !== savedData.id) {
      const changed = new Set<StatChanges>();
      setCargo(savedData.content.cargo.map((c: CargoType) => {
        if (c.type === 'manual' || c.type === 'passengers') {
          return c;
        }
        let id = c.entity.id;
        let [t] = id.split(':');
        let entity: CargoEntity | undefined;
        if (t === '2') {
          entity = ships.find(s => s.id === id);
        } else if (t === '3') {
          entity = vehicles.find(s => s.id === id);
        } else if (t === '16') {
          entity = materials.find(s => s.id === id);
        } else if (t === '12') {
          entity = items.find(s => s.id === id);
        } else if (t === '13') {
          entity = droids.find(s => s.id === id);
        }

        if (entity) {
          if (entity.weight !== c.entity.weight || entity.volume !== c.entity.volume) {
            changed.add({
              name: entity.name,
              mode: 'cargo',
              prevWeight: c.entity.weight,
              newWeight: entity.weight,
              prevVolume: c.entity.volume,
              newVolume: entity.volume
            });
          }

          // older saves stored cargo containers as plain ships
          const type = c.type === 'ship' && 'class' in entity && entity.class === CARGO_CONTAINER_CLASS ? 'container' : c.type;
          return {...c, entity, type};
        }
        return c;
      }));

      setContainerType(savedData.content.containerType);
      setLastProcessedId(savedData.id);

      if (changed.size > 0 && savedData.owner === user.uid) {
        setUpdatedEntities(Array.from(changed));
      }
    }
  }, [savedData, isPending, ships, vehicles, materials, items, droids]);


  const currentData = useMemo(() => ({cargo, containerType}), [cargo, containerType]);


  const onParseData = useCallback((data: Record<string, number>) => {
    track('cargo:paste', {items: Object.keys(data).length});
    Object.entries(data).forEach(([key, value]) => {
      addEntity(key, value);
    })
  }, [addEntity])

  return (
    <Page>
      <title>Cargo Calculator</title>
      {!isReady ? <LoaderScreen message="Loading data"/> : null}
      <Row align="center" justify="between">
        <Col>
          <h2>Cargo Calculator</h2>
          {savedData ? <h3>{savedData.name} <small>({savedData.ownerName})</small></h3> : null}
        </Col>
        <Col>
          <CargoParser onData={onParseData} disabled={!(user.subLevel > 0)}/>
        </Col>
        <Col>
          <SharedSave type="cargo" content={currentData} savedData={savedData}/>
        </Col>
      </Row>
      <Paper shadow="xs" p="md" withBorder>
        <Row wrap={false} mb={2} mobileCol={true} align="end">
          <Col grow={1}>
            <Select data={treeOptions} value={null} onChange={val => {
              if (val) {
                addEntity(val);
                track('cargo:entity-add', {containerType});
              }
            }} searchable
                    placeholder="Add Cargo"/>
          </Col>
          <Col basis="50%">
            <Row align="center">
              <Col grow={1}><Row wrap={false}><span>Carrier type:</span><Select data={optContainerType} value={containerType}
                                                                                onChange={val => { if (val) setContainerType(val); }}/></Row></Col>
              <Col grow={1}><Row wrap={false}><span>Order by: </span><Select data={optOrder} value={order}
                                                                             onChange={val => { if (val) setOrder(val as SortShip); }}/></Row></Col>
            </Row>
          </Col>
        </Row>
        <Row wrap={false} mobileCol={true}>
          <Col grow={1}>
            <h4>Cargo</h4>
            {cargo.map(c => <CargoRow key={c.uuid} cargo={c} updateQty={updateQty} updateManual={updateManual} remove={removeCargo}/>)}
          </Col>
          <Col basis="50%">
            <h4>Possible Carriers</h4>
            {containers.map(c => <ContainerRow key={c.id} container={c} totals={totals} type={containerType}/>)}
          </Col>
        </Row>
      </Paper>

      <StatUpdateDialog
        changes={updatedEntities}
        clear={() => setUpdatedEntities([])}
        save={() => saveMutation.mutate({id, type: 'cargo', content: currentData}, {
          onSuccess: () => setUpdatedEntities([])
        })}
      />
    </Page>
  )

}


type CargoRowProps = {
  cargo: CargoType;
  updateQty: (uuid: string, qty: number | null) => void;
  updateManual: (uuid: string, values: Partial<Omit<ManualEntity, 'id'>>) => void;
  remove: (uuid: string) => void;
}
type ContainerRowProps = {
  container: CalculatedContainerEntity,
  totals: any,
  type: string,
}

const CargoRow = ({cargo, updateQty, updateManual, remove}: CargoRowProps) => {
  if (cargo.type === 'manual') {
    return (
      <Row mb={2} align="center" wrap={false}>
        <Col><div className={style.specialIcon}><ScalesIcon size={40}/></div></Col>
        <Col grow={1}>
          <TextInput value={cargo.entity.name} onChange={e => updateManual(cargo.uuid, {name: e.currentTarget.value})}
                     size="xs" placeholder="Manual cargo" mb={4}/>
          <Row wrap={false}>
            <NumberInput value={cargo.entity.volume} onChange={val => updateManual(cargo.uuid, {volume: typeof val === 'number' ? val : 0})}
                         size="xs" min={0} rightSection={<small>m<sup>3</sup></small>} thousandSeparator/>
            <NumberInput value={cargo.entity.weight} onChange={val => updateManual(cargo.uuid, {weight: typeof val === 'number' ? val : 0})}
                         size="xs" min={0} rightSection={<small>T</small>} thousandSeparator/>
          </Row>
        </Col>
        <Col><ActionIcon onClick={() => remove(cargo.uuid)}><TrashIcon size={16}/></ActionIcon></Col>
      </Row>
    );
  }
  if (cargo.type === 'passengers') {
    return (
      <Row mb={2} align="center" wrap={false}>
        <Col><div className={style.specialIcon}><UsersIcon size={40}/></div></Col>
        <Col grow={1}>{cargo.entity.name}</Col>
        <Col><NumberInput value={cargo.qty ?? ''} onChange={val => updateQty(cargo.uuid, typeof val === 'number' ? val : null)} size="xs" min={0} style={{width: '5em'}}/></Col>
        <Col><ActionIcon onClick={() => remove(cargo.uuid)}><TrashIcon size={16}/></ActionIcon></Col>
      </Row>
    );
  }

  return (
    <Row mb={2} align="center" wrap={false}>
      <Col><img src={cargo.entity.images.small} alt={cargo.entity.name} className={style.img}/></Col>
      <Col grow={1}>{cargo.entity.name}<br/><small>{cargo.entity.volume.toLocaleString()}m<sup>3</sup><br/>{cargo.entity.weight.toLocaleString()}T</small></Col>
      <Col><NumberInput value={cargo.qty ?? ''} onChange={val => updateQty(cargo.uuid, typeof val === 'number' ? val : null)} size="xs" min={0} style={{width: '5em'}}/></Col>
      <Col><ActionIcon onClick={() => remove(cargo.uuid)}><TrashIcon size={16}/></ActionIcon></Col>
    </Row>
  )
}
const ContainerRow = ({container, totals, type}: ContainerRowProps) => {
  return (
    <Row mb={2} wrap={true}>
      <Col><img src={container.images.small} alt={container.name} className={style.img}/></Col>
      <Col grow={1}>{container.name}<br/>
        <Row align="center">
          <Col><DockingBayIcon value={container.dockingbay}/></Col>
          <Col><HangarBayIcon value={container.hangarbay}/></Col>
          {type === 'ships' ? (<><Col><LandingIcon value={container.repulsors}/></Col>
            <Col style={{fontSize: '0.875rem'}}>
              {'hyper' in container.speed ? <div>Hyper: {container.speed.hyper}</div> : null}
              {'sublight' in container.speed ? <div>Sublight: {container.speed.sublight}</div> : null}</Col></>) : null}
        </Row>
        {container.squad > 1 ? <span className="text-red">Squad: {container.squad}</span> : null}
      </Col>
      <Col>
        <CargoBar label="Weight" value={totals.weight} max={container.squad * container.weightcap}/>
        <CargoBar label="Volume" value={totals.volume} max={container.squad * container.volumecap}/>
        {totals.passengers > 0 ? <CargoBar label="Passengers" value={totals.passengers} max={container.squad * container.maxpassengers}/> : null}
      </Col>
    </Row>
  );
}

export default withRouteErrorBoundary(CargoCalculator);
